import { MenuItem, Select, Stack, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import PopupButton from "../common/PopupButton";
import PopupModelWrapper from "../wrappers/PopupModelWrapper";
import useData from "src/hooks/useApi";
import useMessage from "src/hooks/useMessage";
import psl, { ParsedDomain } from "psl";
import { PriceResponse } from "./SelectPriorityModel";
import PopupLoading from "../common/PopupLoading";

type BackorderEdit = {
    open: boolean;
    domainName: string;
    priority: number;
    setOpen: Dispatch<SetStateAction<boolean>>;
    clearSelectedBid: () => void;
    setReloadData: React.Dispatch<React.SetStateAction<number>>;
};

const EditBackorderModel = (props: BackorderEdit) => {
    const {
        open,
        domainName,
        priority,
        setOpen,
        clearSelectedBid,
        setReloadData,
    } = props;

    const { postData } = useData();
    const { showError, showSuccess } = useMessage();

    const [successFee, setSuccessFee] = useState<number>(0);
    const [registerFee, setRegisterFee] = useState<number>(0);

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [newPriority, setNewPriority] = useState<number>(priority);

    const newBid = useMemo(
        () => (registerFee + successFee * newPriority).toFixed(2),
        [registerFee, successFee, newPriority],
    );

    const executeUpdate = async () => {
        try {
            setIsLoading(true);

            const response: any = await postData("/bid/updateBid", {
                domainName: domainName,
                priority: newPriority,
            });

            if (response?.success) {
                showSuccess(
                    `The bid priority for ${domainName} updated successfully`,
                );
                return;
            }

            const error = response?.message || "Bid Priority Update failed";
            showError(error);
        } catch (error: any) {
            showError(`Priority update for ${domainName} domain failed`);
        } finally {
            setIsLoading(false);
            setReloadData(Date.now() / 1000);
            handleClose();
        }
    };

    const setupTLDConfig = async () => {
        try {
            setIsLoading(true);

            const parsedDomain = psl.parse(domainName.trim()) as ParsedDomain;
            const tld = parsedDomain.tld;

            if (!tld) {
                showError(`Invalid TLD Received from the Domain ${domainName}`);
                handleClose();
                return;
            }

            const response: any = await postData("/tld/getPrices", {
                tlds: [tld],
            });

            if (
                !response ||
                !response.success ||
                !response.data ||
                response.data.length !== 1
            ) {
                showError("Error Occurred when fetching TLD Info");
                handleClose();
                return;
            }

            const feeData: PriceResponse = response.data[0];

            setSuccessFee(parseFloat(feeData.successFee));
            setRegisterFee(parseFloat(feeData.regFee));
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        clearSelectedBid();
        setOpen(false);
    };

    useEffect(() => {
        setupTLDConfig();
    }, []);

    if (isLoading) {
        return (
            <PopupModelWrapper
                open={open}
                handleClose={handleClose}
                title="Update Bid"
            >
                <PopupLoading />
            </PopupModelWrapper>
        );
    }

    return (
        <PopupModelWrapper
            open={open}
            handleClose={handleClose}
            title="Update Bid"
        >
            <Stack sx={{ my: 3 }} spacing={1}>
                <Typography
                    sx={{
                        fontWeight: 500,
                        fontSize: "17px",
                        pb: 1,
                        color: "#3A3541CC",
                    }}
                >
                    Confirm your Bid for {domainName} Domain
                </Typography>
                <Stack direction="row" alignItems="center">
                    <Typography
                        width={80}
                        sx={{ fontSize: "14px", color: "#3A354180" }}
                    >
                        Priority
                    </Typography>
                    <Select
                        id="demo-simple-select"
                        value={newPriority}
                        onChange={(e) => {
                            setNewPriority(
                                e.target.value ? +e.target.value : 1,
                            );
                        }}
                        sx={{
                            fontSize: "14px",
                            padding: 0,
                            outlineColor: "white",
                            width: "95px",
                            textAlign: "center",
                            transition: "all 0.3s",
                            "& .MuiOutlinedInput-input": {
                                py: "5px",
                            },
                        }}
                    >
                        <MenuItem value={1}>1</MenuItem>
                        <MenuItem value={2}>2</MenuItem>
                        <MenuItem value={3}>3</MenuItem>
                        <MenuItem value={4}>4</MenuItem>
                        <MenuItem value={5}>5</MenuItem>
                    </Select>
                </Stack>
                <Stack direction="row" alignItems="center">
                    <Typography
                        sx={{ fontSize: "14px", color: "#3A354180" }}
                        width={80}
                    >
                        Bid
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            textAlign: "right",
                            width: "85px",
                            p: "5px",
                            borderRadius: 1,
                            bgcolor: "#E6E6E6",
                            color: "#3A354180",
                        }}
                    >
                        {newBid}
                    </Typography>
                </Stack>
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
                <PopupButton triggerFunction={handleClose} type="cancel">
                    Cancel
                </PopupButton>
                <PopupButton
                    triggerFunction={executeUpdate}
                    type="proceed"
                    disabled={isLoading}
                >
                    Proceed
                </PopupButton>
            </Stack>
        </PopupModelWrapper>
    );
};

export default EditBackorderModel;
